import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { observer } from "mobx-react";
import * as React from "react";
import { HXSimpleDialog } from "./HXSimpleDialog";
import { FlexColumn } from "./common.styles";
import InputAdornment from "@mui/material/InputAdornment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";
import { LeftAlignedButton, StudyLinkDialogButtonsContainer, StudyLinkDialogCopyTextField, StudyLinkDialogEnrollmentAndPrescreeningTextContainer, StudyLinkDialogQRCodeCanvas, StudyLinkDialogSection, StudyLinkDialogTabAutocomplete, } from "./StudyLinkDialog.styles";
import { StudyLinkDialogViewModel } from "./StudyLinkDialogViewModel";
import { useViewModel } from "./useViewModel";
import { HXButton } from "./HXButton";
import { downloadCanvasAsPNG } from "./DownloadHelpers";
import { BoldText, LighterText } from "./HXDesktopPage.styles";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import { enumValues } from "healthex-common/utils";
export const StudyLinkDialogView = observer((props) => {
    const { container, open, onClose, study } = props;
    const model = useViewModel(StudyLinkDialogViewModel, { container, study });
    return (_jsx(HXSimpleDialog, { open: open, onClose: onClose, title: model.title, maxTitleLines: 3, closeChoice: true, buttons: [], width: model.width, hasCustomContentAndActions: true, children: _jsx(StudyLinkView, { container: container, study: study }) }));
});
var LinkType;
(function (LinkType) {
    LinkType["INITIAL_CONSENT"] = "INITIAL_CONSENT";
    LinkType["UPDATE"] = "UPDATE";
    LinkType["ADD_RECORD_LOCATION"] = "ADD_RECORD_LOCATION";
    LinkType["RESUME"] = "RESUME";
})(LinkType || (LinkType = {}));
const LINK_TYPE_LABELS = {
    [LinkType.INITIAL_CONSENT]: "Initial consent link",
    [LinkType.UPDATE]: "Update link",
    [LinkType.ADD_RECORD_LOCATION]: "Add a provider link",
    [LinkType.RESUME]: "Resume link",
};
const LINK_TYPE_SECTIONS = {
    [LinkType.INITIAL_CONSENT]: {
        link: (model) => model.initialConsentLink,
        downloadName: (model) => model.initialConsentDownloadName,
        text: "Let patients consent to retrieve and share their data with your organization.",
    },
    [LinkType.UPDATE]: {
        link: (model) => model.updateLink,
        downloadName: (model) => model.updateDownloadName,
        text: "Let already consented patients share an updated copy of their health records.",
    },
    [LinkType.ADD_RECORD_LOCATION]: {
        link: (model) => model.addRecordLocationLink,
        downloadName: (model) => model.addRecordLocationDownloadName,
        text: "Let already consented patients add a provider without a full update.",
    },
    [LinkType.RESUME]: {
        link: (model) => model.resumeLink,
        downloadName: (model) => model.resumeDownloadName,
        text: "Let patients pick up where they left off if they didn't complete the flow.",
    },
};
export const StudyLinkView = observer((props) => {
    const { container, study, onBack } = props;
    const model = useViewModel(StudyLinkDialogViewModel, { container, study });
    const [tab, setTab] = React.useState(LinkType.INITIAL_CONSENT);
    const section = LINK_TYPE_SECTIONS[tab];
    return (_jsxs(_Fragment, { children: [_jsx(StudyLinkDialogTabAutocomplete, { label: "Link type", options: enumValues(LinkType), getId: (value) => value, getDisplayValue: (value) => LINK_TYPE_LABELS[value], value: tab, onChange: (value) => value && setTab(value) }), _jsx(Section, { model: model, link: section.link(model), downloadName: section.downloadName(model), gap: 16, text: section.text, footerJustifyContent: "flex-end", onBack: onBack })] }));
});
export const Section = observer((props) => {
    const { model, link, downloadName, gap, footerJustifyContent, width, copyTextFieldWidth, title, text, onBack } = props;
    const qrCodeRef = React.useRef(null);
    return (_jsxs(StudyLinkDialogSection, { gap: gap, width: width, children: [_jsx(FlexColumn, { gap: 8, alignItems: "left", children: text && (_jsxs(StudyLinkDialogEnrollmentAndPrescreeningTextContainer, { gap: 4, style: { marginTop: 16 }, children: [title && (_jsx(BoldText, { textAlign: "left", style: { whiteSpace: "nowrap" }, children: title })), _jsx(LighterText, { textAlign: "left", style: { whiteSpace: "nowrap" }, children: text })] })) }), _jsx(FlexColumn, { gap: 8, alignItems: "center", children: _jsx(StudyLinkDialogQRCodeCanvas, { ref: qrCodeRef, value: link, size: 300, marginSize: 2 }) }), _jsxs(FlexColumn, { gap: 28, alignItems: "center", children: [_jsx(StudyLinkDialogCopyTextField, { value: link, InputProps: {
                            startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(ContentCopyIcon, {}) })),
                        }, onClick: () => model.copyLink(link), width: copyTextFieldWidth }), _jsxs(StudyLinkDialogButtonsContainer, { justifyContent: footerJustifyContent, children: [onBack && (_jsx(LeftAlignedButton, { secondary: true, startIcon: _jsx(ChevronLeft, {}), onClick: onBack, children: "Back" })), _jsx(HXButton, { text: true, startIcon: _jsx(LinkIcon, {}), onClick: () => model.copyLink(link), children: "Copy link" }), _jsx(HXButton, { startIcon: _jsx(DownloadIcon, {}), onClick: () => {
                                    if (!qrCodeRef.current)
                                        return;
                                    downloadCanvasAsPNG(qrCodeRef.current, downloadName);
                                }, children: "Download QR code" })] })] })] }));
});
